import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { PAYMENT_METHODS } from './flexpayService';

const StepPaiement = ({
    customerInfo,
    cityData,
    deliveryData,
    selectedPayment,
    setSelectedPayment,
    paymentPhone,
    setPaymentPhone,
    cardInfo,
    setCardInfo,
    canProceed,
    total,
    onBack,
    onPay,
}) => {
    return (
        <motion.div
            key="step-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            {/* Delivery summary */}
            <div className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-bold text-lg flex items-center gap-2">
                        <Icon icon="solar:delivery-bold-duotone" className="text-lime-500 text-xl" />
                        Récapitulatif livraison
                    </h2>
                    <button
                        onClick={onBack}
                        className="text-lime-600 dark:text-lime-400 text-xs font-semibold hover:underline"
                    >
                        Modifier
                    </button>
                </div>
                <div className="bg-slate-50 dark:bg-store-gray-dark/50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-store-gray">Client</span>
                        <span className="font-medium">{customerInfo.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-store-gray">Téléphone</span>
                        <span className="font-medium">{customerInfo.phone}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-store-gray">Ville</span>
                        <span className="font-medium">{cityData?.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-store-gray">Mode</span>
                        <span className="font-medium">{deliveryData?.label}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-store-gray">Délai</span>
                        <span className="font-medium text-lime-600 dark:text-lime-400">{deliveryData?.delay}</span>
                    </div>
                    {customerInfo.address && (
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-store-gray">Adresse</span>
                            <span className="font-medium text-right max-w-[200px]">{customerInfo.address}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment method selection */}
            <div className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4">
                <h2 className="font-bold text-lg flex items-center gap-2">
                    <Icon icon="solar:wallet-bold-duotone" className="text-lime-500 text-xl" />
                    Méthode de paiement
                </h2>
                <div className="space-y-3">
                    {PAYMENT_METHODS.map((method) => (
                        <button
                            key={method.id}
                            disabled={method.disabled}
                            onClick={() => !method.disabled && setSelectedPayment(method.id)}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left ${
                                method.disabled
                                    ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-store-gray-dark/20 border-slate-200 dark:border-store-border'
                                    : selectedPayment === method.id
                                        ? 'bg-lime-500/10 border-lime-500 shadow-sm'
                                        : 'bg-slate-50 dark:bg-store-gray-dark/50 border-slate-200 dark:border-store-border hover:border-lime-500/40'
                                }`}
                        >
                            <div
                                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                    method.disabled
                                        ? 'bg-slate-100 dark:bg-store-gray-dark/50 text-slate-300 dark:text-store-gray/50'
                                        : selectedPayment === method.id
                                            ? 'bg-lime-500/20 text-lime-500'
                                            : 'bg-slate-200 dark:bg-store-gray-dark text-slate-400 dark:text-store-gray'
                                    }`}
                            >
                                <Icon icon={method.icon} className="text-2xl" />
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-sm text-slate-900 dark:text-white">
                                    {method.label}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-store-gray">
                                    {method.description}
                                </p>
                            </div>
                            <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    method.disabled
                                        ? 'border-slate-200 dark:border-store-gray/50'
                                        : selectedPayment === method.id
                                            ? 'border-lime-500 bg-lime-500'
                                            : 'border-slate-300 dark:border-store-gray'
                                    }`}
                            >
                                {selectedPayment === method.id && (
                                    <Icon icon="solar:check-circle-bold" className="text-black text-sm" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Payment form - depends on selected method */}
            <AnimatePresence mode="wait">
                {selectedPayment === 'mobile-money' && (
                    <motion.div
                        key="mobile-form"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4"
                    >
                        <h2 className="font-bold text-lg flex items-center gap-2">
                            <Icon icon="solar:phone-bold-duotone" className="text-lime-500 text-xl" />
                            Numéro Mobile Money
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-store-gray">
                            Saisissez le numéro depuis lequel le paiement sera effectué. Vous recevrez une notification USSD sur ce numéro pour confirmer.
                        </p>
                        <input
                            type="tel"
                            value={paymentPhone}
                            onChange={(e) => setPaymentPhone(e.target.value.replace(/\D/g, ''))}
                            placeholder="243971234567"
                            className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-3 text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                        />
                        <div className="flex gap-2 flex-wrap">
                            {['Airtel Money', 'M-Pesa', 'Orange Money'].map((op) => (
                                <span
                                    key={op}
                                    className="text-[10px] px-2 py-1 rounded-full bg-slate-100 dark:bg-store-gray-dark text-slate-500 dark:text-store-gray border border-slate-200 dark:border-store-border"
                                >
                                    {op}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                )}

                {selectedPayment === 'card' && (
                    <motion.div
                        key="card-form"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-5 space-y-4"
                    >
                        <h2 className="font-bold text-lg flex items-center gap-2">
                            <Icon icon="solar:card-bold-duotone" className="text-lime-500 text-xl" />
                            Informations de carte
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                                    Titulaire de la carte *
                                </label>
                                <input
                                    type="text"
                                    value={cardInfo.holderName}
                                    onChange={(e) =>
                                        setCardInfo({ ...cardInfo, holderName: e.target.value })
                                    }
                                    placeholder="Nom tel qu'il apparaît sur la carte"
                                    className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                                    Numéro de carte *
                                </label>
                                <input
                                    type="text"
                                    value={cardInfo.number}
                                    onChange={(e) => {
                                        const v = e.target.value.replace(/\D/g, '').slice(0, 16);
                                        setCardInfo({ ...cardInfo, number: v.replace(/(.{4})/g, '$1 ').trim() });
                                    }}
                                    placeholder="1234 5678 9012 3456"
                                    maxLength={19}
                                    className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                                        Expiration *
                                    </label>
                                    <input
                                        type="text"
                                        value={cardInfo.expiry}
                                        onChange={(e) => {
                                            let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                            if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                            setCardInfo({ ...cardInfo, expiry: v });
                                        }}
                                        placeholder="MM/YY"
                                        maxLength={5}
                                        className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500 dark:text-store-gray mb-1 block font-medium">
                                        CVV *
                                    </label>
                                    <input
                                        type="password"
                                        value={cardInfo.cvv}
                                        onChange={(e) =>
                                            setCardInfo({ ...cardInfo, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })
                                        }
                                        placeholder="•••"
                                        maxLength={4}
                                        className="w-full bg-slate-50 dark:bg-store-gray-dark border border-slate-200 dark:border-store-border rounded-xl px-4 py-2.5 text-sm font-mono text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-store-gray focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 dark:text-store-gray pt-1">
                            <Icon icon="solar:lock-bold" className="text-sm text-lime-500" />
                            Paiement sécurisé — vos données sont chiffrées (PCI DSS)
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Action buttons */}
            <div className="flex gap-3">
                <button
                    onClick={onBack}
                    className="px-6 py-3.5 rounded-xl border border-slate-200 dark:border-store-border text-sm font-semibold text-slate-600 dark:text-store-gray hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-store-gray transition-all"
                >
                    <Icon icon="guidance:right-arrow" className="inline mr-1" />
                    Retour
                </button>
                <motion.button
                    onClick={onPay}
                    disabled={!canProceed}
                    whileHover={canProceed ? { scale: 1.01 } : {}}
                    whileTap={canProceed ? { scale: 0.98 } : {}}
                    className={`flex-1 py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${canProceed
                        ? 'bg-lime-500 hover:bg-lime-400 text-black shadow-lg shadow-lime-500/20'
                        : 'bg-slate-200 dark:bg-store-gray-dark text-slate-400 dark:text-store-gray cursor-not-allowed'
                        }`}
                >
                    <Icon icon="solar:lock-bold" />
                    Payer {total.toFixed(2)} $US
                </motion.button>
            </div>
        </motion.div>
    );
};

export default StepPaiement;
